import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useEffect, useState } from "react";

export default function PlanningWeek() {
    const [plannings, setPlannings] = useState([]);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [profil, setProfil] = useState("all"); // 👈 "all" par défaut
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // ✅ Lundi → Dimanche (local, sans décalage UTC)
    const getWeekRange = (date) => {
        const day = date.getDay(); // 0 = dimanche, 1 = lundi, ...
        const diffToMonday = day === 0 ? -6 : 1 - day;

        const monday = new Date(date);
        monday.setHours(0, 0, 0, 0);
        monday.setDate(date.getDate() + diffToMonday);

        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        sunday.setHours(23, 59, 59, 999);

        return { start: monday, end: sunday };
    };

    // ✅ Format local sans conversion UTC
    const formatDate = (date) => date.toLocaleDateString("fr-CA"); // YYYY-MM-DD

    // 🔹 Fetch du planning
    useEffect(() => {
        const fetchPlanning = async () => {
            setLoading(true);
            try {
                const { start, end } = getWeekRange(currentDate);
                const endPlusOne = new Date(end);
                endPlusOne.setDate(endPlusOne.getDate() + 1);

                const url = `https://back.vitaleau.fr/api/planning/creneaux?start=${formatDate(start)}&end=${formatDate(endPlusOne)}`;

                const response = await fetch(url);
                if (!response.ok) throw new Error("Erreur réseau ou serveur");
                const data = await response.json();
                setPlannings(data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchPlanning();
    }, [currentDate]);

    // 🔹 Regroupe les séances par jour
    const groupByDay = (items) => {
        const grouped = {};
        items.forEach((p) => {
            const dateKey = p.json?.date ? new Date(p.json.date).toLocaleDateString("fr-CA") : null;
            if (!dateKey) return;
            if (!grouped[dateKey]) grouped[dateKey] = [];
            grouped[dateKey].push(p);
        });
        return grouped;
    };

    // 🔹 Filtrage local selon profil
    const filteredPlannings =
        profil === "all" ? plannings : plannings.filter((p) => p.json?.activite?.profil?.toLowerCase().includes(profil.toLowerCase()));

    const groupedPlannings = groupByDay(filteredPlannings);

    // 🔹 Génère les 7 jours de la semaine
    const getWeekDays = (date) => {
        const { start } = getWeekRange(date);
        return Array.from({ length: 7 }).map((_, i) => {
            const d = new Date(start);
            d.setDate(start.getDate() + i);
            return d;
        });
    };

    const weekDays = getWeekDays(currentDate);

    // 🔹 Navigation semaine précédente / suivante
    const changeWeek = (offset) => {
        const newDate = new Date(currentDate);
        newDate.setDate(newDate.getDate() + offset * 7);
        setCurrentDate(newDate);
    };

    // 🔹 Aller à la semaine actuelle
    const goToToday = () => setCurrentDate(new Date());

    // 🔹 Date actuelle formatée
    const todayKey = formatDate(new Date());

    // 🔹 Libellé de la semaine
    const { start, end } = getWeekRange(currentDate);
    const weekLabel = `${start.toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "short",
    })} - ${end.toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    })}`;

    return (
        <section className="planning-section">
            <div className="container">
                {/* En-tête navigation + filtre */}
                <div className="header">
                    <h2>{weekLabel.toUpperCase()}</h2>

                    <div className="filter">
                        <select id="profil" value={profil} onChange={(e) => setProfil(e.target.value)}>
                            <option value="all">Tous</option>
                            <option value="adulte">Adultes</option>
                            <option value="enfant">Enfants</option>
                        </select>
                    </div>
                    <div className="container-action-button-planning">
                        <button onClick={() => changeWeek(-1)} className="nav-btn">
                            <ArrowBackIosIcon />
                        </button>
                        <button onClick={goToToday} className="today-btn">
                            Aujourd’hui
                        </button>
                        <button onClick={() => changeWeek(1)} className="nav-btn">
                            <ArrowForwardIosIcon />
                        </button>
                    </div>
                </div>

                {loading && <p>Chargement du planning...</p>}
                {error && <p style={{ color: "red" }}>Erreur : {error}</p>}

                {!loading && !error && (
                    <div className="calendar-grid">
                        {weekDays.map((date) => {
                            const dateKey = formatDate(date);
                            const sessions = groupedPlannings[dateKey] || [];
                            const dayName = date.toLocaleDateString("fr-FR", { weekday: "long" });
                            const isToday = dateKey === todayKey;

                            return (
                                <div key={dateKey} className={`day-column ${isToday ? "today" : ""}`}>
                                    <div className="day-header">
                                        <strong>{dayName}</strong>
                                    </div>
                                    <div className="day-body">
                                        {sessions.length > 0 ? (
                                            sessions
                                                .sort((a, b) => new Date(a.start) - new Date(b.start))
                                                .map((p, i) => {
                                                    const start = new Date(p.json.heure_debut).toLocaleTimeString("fr-FR", {
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                    });
                                                    const end = new Date(p.json.heure_fin).toLocaleTimeString("fr-FR", {
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                    });
                                                    const activity = p.json?.activite?.nom || p.title;
                                                    const profile = p.json?.activite?.profil || "";
                                                    return (
                                                        <div key={i} className={`session ${profile.toLowerCase()}`}>
                                                            <span className="time">
                                                                {start} - {end}
                                                            </span>
                                                            <div className="activity">
                                                                <strong>{activity}</strong>
                                                                <small>{profile}</small>
                                                            </div>
                                                        </div>
                                                    );
                                                })
                                        ) : (
                                            <div className="no-session">—</div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </section>
    );
}
